import { gql } from "@apollo/client";
import { add, Money, ratio } from "@asmbl/shared/money";
import { formatNumeral, mapMaybe } from "@asmbl/shared/utils";
import {
  ActualVariablePayChange_cashCompensation,
  ActualVariablePayChange_recItem,
  CashCompType,
  RecItemType,
} from "../../../__generated__/graphql";
import { getTotalCash } from "../../../models/CashCompensation";
import {
  CompItemRecommendationTypeTitle,
  whereType,
} from "../../../models/CompRecommendation";
import { getSimpleCashLabel } from "../../../models/Currency";
import { AssembleTypography } from "../../AssembleTypography";
import { CompComponentContainer } from "./CompComponentContainer";
import { BadgedMoneyValue, CompValue } from "./CompValue";
import { LabelValue } from "./LabelValue";

type RecItemWithValue = ActualVariablePayChange_recItem & {
  recommendedCashValue: Money;
};

export function ActualVariablePayChange({
  cashCompensations,
  recItems,
}: {
  cashCompensations: ActualVariablePayChange_cashCompensation[];
  recItems: ActualVariablePayChange_recItem[];
}): JSX.Element | null {
  const actualRecurringBonusRecItem = recItems
    .filter(
      (item): item is RecItemWithValue => item.recommendedCashValue !== null
    )
    .find(whereType(RecItemType.ACTUAL_RECURRING_BONUS));
  const actualRecurringCompanyBonusRecItem = recItems
    .filter(
      (item): item is RecItemWithValue => item.recommendedCashValue !== null
    )
    .find(whereType(RecItemType.ACTUAL_RECURRING_BONUS_COMPANY));
  const actualRecurringCustomBonusRecItem = recItems
    .filter(
      (item): item is RecItemWithValue => item.recommendedCashValue !== null
    )
    .find(whereType(RecItemType.ACTUAL_RECURRING_BONUS_CUSTOM));
  const meritBonusRecItem = recItems
    .filter(
      (item): item is RecItemWithValue => item.recommendedCashValue !== null
    )
    .find(whereType(RecItemType.MERIT_BONUS));

  if (
    actualRecurringBonusRecItem == null &&
    meritBonusRecItem == null &&
    actualRecurringCompanyBonusRecItem == null &&
    actualRecurringCustomBonusRecItem == null
  ) {
    return null;
  }

  const actualRecItems = mapMaybe(
    [
      actualRecurringBonusRecItem,
      actualRecurringCompanyBonusRecItem,
      actualRecurringCustomBonusRecItem,
      meritBonusRecItem,
    ],
    (i) => i
  );

  const currentTarget = getTotalCash(cashCompensations)?.subcomponents.find(
    (item) => item.type === CashCompType.RECURRING_BONUS
  )?.annualCashEquivalent;
  let actualRecurringTargetRatio = null;
  let actualCompanyRecurringTargetRatio = null;
  let actualCustomRecurringTargetRatio = null;

  if (currentTarget) {
    actualRecurringTargetRatio = actualRecurringBonusRecItem
      ? ratio(actualRecurringBonusRecItem.recommendedCashValue, currentTarget)
      : null;
    actualCompanyRecurringTargetRatio = actualRecurringCompanyBonusRecItem
      ? ratio(
          actualRecurringCompanyBonusRecItem.recommendedCashValue,
          currentTarget
        )
      : null;
    actualCustomRecurringTargetRatio = actualRecurringCustomBonusRecItem
      ? ratio(
          actualRecurringCustomBonusRecItem.recommendedCashValue,
          currentTarget
        )
      : null;
  }

  return (
    <CompComponentContainer>
      <LabelValue
        label={
          <AssembleTypography variant="productEyebrowSmall" gutterBottom>
            Actual Variable Pay
          </AssembleTypography>
        }
        compValue={
          <BadgedMoneyValue
            value={actualRecItems
              .map((item) => item.recommendedCashValue)
              .reduce(add)}
          />
        }
      />
      {actualRecurringBonusRecItem && (
        <LabelValue
          label={
            CompItemRecommendationTypeTitle[
              actualRecurringBonusRecItem.recommendationType
            ]
          }
          compValue={
            <CompValue
              value={`${getSimpleCashLabel(
                actualRecurringBonusRecItem.recommendedCashValue
              )}${
                actualRecurringTargetRatio != null
                  ? ` | ${formatNumeral(actualRecurringTargetRatio, {
                      style: "percent",
                    })} current target bonus`
                  : ""
              }`}
            />
          }
        />
      )}
      {actualRecurringCompanyBonusRecItem && (
        <LabelValue
          label={
            CompItemRecommendationTypeTitle[
              actualRecurringCompanyBonusRecItem.recommendationType
            ]
          }
          compValue={
            <CompValue
              value={`${getSimpleCashLabel(
                actualRecurringCompanyBonusRecItem.recommendedCashValue
              )}${
                actualCompanyRecurringTargetRatio != null
                  ? ` | ${formatNumeral(actualCompanyRecurringTargetRatio, {
                      style: "percent",
                    })} current target bonus`
                  : ""
              }`}
            />
          }
        />
      )}
      {actualRecurringCustomBonusRecItem && (
        <LabelValue
          label={
            CompItemRecommendationTypeTitle[
              actualRecurringCustomBonusRecItem.recommendationType
            ]
          }
          compValue={
            <CompValue
              value={`${getSimpleCashLabel(
                actualRecurringCustomBonusRecItem.recommendedCashValue
              )}${
                actualCustomRecurringTargetRatio != null
                  ? ` | ${formatNumeral(actualCustomRecurringTargetRatio, {
                      style: "percent",
                    })} current target bonus`
                  : ""
              }`}
            />
          }
        />
      )}
      {meritBonusRecItem && (
        <LabelValue
          label={
            CompItemRecommendationTypeTitle[
              meritBonusRecItem.recommendationType
            ]
          }
          compValue={
            <CompValue
              value={getSimpleCashLabel(meritBonusRecItem.recommendedCashValue)}
            />
          }
        />
      )}
    </CompComponentContainer>
  );
}

ActualVariablePayChange.fragments = {
  recItem: gql`
    fragment ActualVariablePayChange_recItem on RecItem {
      recommendationType
      recommendedCashValue
      recommendedPercentValue
      unitType
    }
  `,
  cashCompensation: gql`
    fragment ActualVariablePayChange_cashCompensation on CashCompensation {
      type
      annualCashEquivalent
      hourlyCashEquivalent
      unit
      percentOfSalary
    }
  `,
};
